import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from 'react';

/**
 * DrawCanvas - a point-and-click SVG drawing surface for geometry diagrams.
 * Tools: select/move, circle, rectangle, triangle, line, arrow, pen, text.
 * Shapes snap to a 10px grid, can be moved, resized via handles, deleted,
 * and undone. Exports exactly what you see as a crisp PNG (no external
 * fonts or compilers involved, so the attachment always matches the canvas).
 */

const W = 800;
const H = 480;
const GRID = 10;
const EXPORT_SCALE = 3;
const COLORS = ['#111827', '#00adb5', '#ef4444', '#3b82f6', '#16a34a', '#f59e0b'];
const FONT_SIZES = [14, 20, 28];
const FONT_FAMILY = "Georgia, 'Times New Roman', serif";

const snap = v => Math.round(v / GRID) * GRID;
let nextId = 1;

const TOOLS = [
  { id: 'select', label: '▲', title: 'Select / Move' },
  { id: 'circle', label: '◯', title: 'Circle — click to draw, or drag me onto the canvas', draggable: true },
  { id: 'rect', label: '▭', title: 'Rectangle — click to draw, or drag me onto the canvas', draggable: true },
  { id: 'triangle', label: '△', title: 'Triangle — click to draw, or drag me onto the canvas', draggable: true },
  { id: 'line', label: '╱', title: 'Line — click to draw, or drag me onto the canvas', draggable: true },
  { id: 'arrow', label: '↗', title: 'Arrow — click to draw, or drag me onto the canvas', draggable: true },
  { id: 'pen', label: '✎', title: 'Freehand pen' },
  { id: 'text', label: 'T', title: 'Text — click to place, or drag me onto the canvas', draggable: true },
];

// Ready-made shape dropped from the toolbar at point p
function makeDroppedShape(type, p, style) {
  const base = { id: nextId++, type, ...style };
  switch (type) {
    case 'circle':
      return { ...base, x1: p.x, y1: p.y, x2: p.x + 60, y2: p.y };
    case 'rect':
      return { ...base, x1: p.x - 70, y1: p.y - 45, x2: p.x + 70, y2: p.y + 45 };
    case 'triangle': {
      const t = { ...base, x1: p.x - 60, y1: p.y - 50, x2: p.x + 60, y2: p.y + 50 };
      return { ...t, points: triangleToPoints(t) };
    }
    case 'line':
      return { ...base, x1: p.x - 80, y1: p.y, x2: p.x + 80, y2: p.y };
    case 'arrow':
      return { ...base, x1: p.x - 80, y1: p.y, x2: p.x + 80, y2: p.y };
    default:
      return null;
  }
}

function shapeBounds(s) {
  if (s.type === 'circle') {
    const r = Math.hypot(s.x2 - s.x1, s.y2 - s.y1);
    return { minX: s.x1 - r, minY: s.y1 - r, maxX: s.x1 + r, maxY: s.y1 + r };
  }
  if (s.type === 'pen' || (s.type === 'triangle' && s.points)) {
    const xs = s.points.map(p => p.x);
    const ys = s.points.map(p => p.y);
    return {
      minX: Math.min(...xs),
      minY: Math.min(...ys),
      maxX: Math.max(...xs),
      maxY: Math.max(...ys),
    };
  }
  if (s.type === 'text') {
    const w = (s.text?.length || 1) * s.fontSize * 0.6;
    return { minX: s.x1, minY: s.y1 - s.fontSize, maxX: s.x1 + w, maxY: s.y1 + 4 };
  }
  return {
    minX: Math.min(s.x1, s.x2),
    minY: Math.min(s.y1, s.y2),
    maxX: Math.max(s.x1, s.x2),
    maxY: Math.max(s.y1, s.y2),
  };
}

function translateShape(s, dx, dy) {
  const moved = { ...s, x1: s.x1 + dx, y1: s.y1 + dy, x2: s.x2 + dx, y2: s.y2 + dy };
  if (s.points) moved.points = s.points.map(p => ({ x: p.x + dx, y: p.y + dy }));
  return moved;
}

// Bounding-box triangle → three explicit, individually editable vertices
function triangleToPoints(s) {
  const x = Math.min(s.x1, s.x2);
  const y = Math.min(s.y1, s.y2);
  const w = Math.max(1, Math.abs(s.x2 - s.x1));
  const h = Math.max(1, Math.abs(s.y2 - s.y1));
  return [
    { x: x + w / 2, y },
    { x, y: y + h },
    { x: x + w, y: y + h },
  ];
}

function ShapeEl({ s }) {
  const fill = s.fillOn ? `${s.stroke}26` : 'none';
  const common = {
    stroke: s.stroke,
    strokeWidth: s.strokeWidth,
    fill,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  };
  switch (s.type) {
    case 'circle': {
      const r = Math.max(1, Math.hypot(s.x2 - s.x1, s.y2 - s.y1));
      return <circle cx={s.x1} cy={s.y1} r={r} {...common} />;
    }
    case 'rect': {
      const x = Math.min(s.x1, s.x2);
      const y = Math.min(s.y1, s.y2);
      return (
        <rect
          x={x}
          y={y}
          width={Math.max(1, Math.abs(s.x2 - s.x1))}
          height={Math.max(1, Math.abs(s.y2 - s.y1))}
          {...common}
        />
      );
    }
    case 'triangle': {
      const pts = s.points || triangleToPoints(s);
      return <polygon points={pts.map(p => `${p.x},${p.y}`).join(' ')} {...common} />;
    }
    case 'line':
      return <line x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} {...common} fill="none" />;
    case 'arrow':
      return (
        <line
          x1={s.x1}
          y1={s.y1}
          x2={s.x2}
          y2={s.y2}
          {...common}
          fill="none"
          markerEnd={`url(#arrow-${s.stroke.slice(1)})`}
        />
      );
    case 'pen':
      return (
        <polyline points={s.points.map(p => `${p.x},${p.y}`).join(' ')} {...common} fill="none" />
      );
    case 'text':
      return (
        <text
          x={s.x1}
          y={s.y1}
          fontSize={s.fontSize}
          fontFamily={FONT_FAMILY}
          fontStyle="italic"
          fill={s.stroke}
        >
          {s.text}
        </text>
      );
    default:
      return null;
  }
}

// Resize handles for the selected shape
function Handles({ s, onHandleDown }) {
  const sq = (x, y, handle) => (
    <rect
      key={handle}
      x={x - 5}
      y={y - 5}
      width={10}
      height={10}
      fill="#fff"
      stroke="#00adb5"
      strokeWidth={1.5}
      style={{ cursor: 'grab' }}
      onPointerDown={e => onHandleDown(e, handle)}
    />
  );
  if (s.type === 'circle') return sq(s.x2, s.y2, 'end'); // radius handle
  if (s.type === 'line' || s.type === 'arrow')
    return (
      <>
        {sq(s.x1, s.y1, 'start')}
        {sq(s.x2, s.y2, 'end')}
      </>
    );
  if (s.type === 'triangle' && s.points)
    return <>{s.points.map((p, i) => sq(p.x, p.y, `pt-${i}`))}</>; // each vertex editable
  if (s.type === 'rect' || s.type === 'triangle') {
    const x1 = Math.min(s.x1, s.x2);
    const x2 = Math.max(s.x1, s.x2);
    const y1 = Math.min(s.y1, s.y2);
    const y2 = Math.max(s.y1, s.y2);
    return (
      <>
        {sq(x1, y1, 'nw')}
        {sq(x2, y1, 'ne')}
        {sq(x1, y2, 'sw')}
        {sq(x2, y2, 'se')}
      </>
    );
  }
  return null;
}

const DrawCanvas = forwardRef(function DrawCanvas({ onShapesChange }, ref) {
  const [shapes, setShapes] = useState([]);
  const [history, setHistory] = useState([]);
  const [tool, setTool] = useState('circle');
  const [stroke, setStroke] = useState(COLORS[0]);
  const [strokeWidth, setStrokeWidth] = useState(2.5);
  const [fillOn, setFillOn] = useState(false);
  const [fontSize, setFontSize] = useState(20);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [textEdit, setTextEdit] = useState(null); // { x, y, value, id? }
  const dragRef = useRef(null);
  const svgRef = useRef(null);

  useEffect(() => {
    onShapesChange?.(shapes.length);
  }, [shapes, onShapesChange]);

  const commit = useCallback(
    next => {
      setHistory(h => [...h.slice(-40), shapes]);
      setShapes(next);
    },
    [shapes]
  );

  const getPoint = e => {
    const rect = svgRef.current.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * W,
      y: ((e.clientY - rect.top) / rect.height) * H,
    };
  };

  const commitText = useCallback(() => {
    setTextEdit(te => {
      if (te && te.value.trim()) {
        const shape = {
          id: te.id ?? nextId++,
          type: 'text',
          x1: te.x,
          y1: te.y,
          x2: te.x,
          y2: te.y,
          text: te.value.trim(),
          stroke,
          strokeWidth,
          fontSize,
        };
        setHistory(h => [...h.slice(-40), shapes]);
        setShapes(prev =>
          te.id != null ? prev.map(s => (s.id === te.id ? shape : s)) : [...prev, shape]
        );
      }
      return null;
    });
  }, [shapes, stroke, strokeWidth, fontSize]);

  // ---- pointer flow ----
  const handleCanvasDown = e => {
    if (textEdit) {
      commitText();
      return;
    }
    const raw = getPoint(e);
    const p = { x: snap(raw.x), y: snap(raw.y) };
    svgRef.current.setPointerCapture(e.pointerId);

    if (tool === 'text') {
      setTextEdit({ x: p.x, y: p.y, value: '' });
      return;
    }
    if (tool === 'select') {
      setSelectedId(null);
      return;
    }
    const shape = {
      id: nextId++,
      type: tool,
      x1: p.x,
      y1: p.y,
      x2: p.x,
      y2: p.y,
      points: tool === 'pen' ? [raw] : undefined,
      stroke,
      strokeWidth,
      fillOn,
    };
    dragRef.current = { mode: 'draw' };
    setDraft(shape);
  };

  const handleShapeDown = (e, id) => {
    if (tool !== 'select') return;
    e.stopPropagation();
    svgRef.current.setPointerCapture(e.pointerId);
    setSelectedId(id);
    const orig = shapes.find(s => s.id === id);
    dragRef.current = { mode: 'move', start: getPoint(e), orig };
  };

  const handleHandleDown = (e, handle) => {
    e.stopPropagation();
    svgRef.current.setPointerCapture(e.pointerId);
    const orig = shapes.find(s => s.id === selectedId);
    dragRef.current = { mode: 'resize', handle, orig };
  };

  // Drop a ready-made shape dragged from the toolbar
  const handleShapeDrop = e => {
    const type = e.dataTransfer.getData('text/x-shape');
    if (!type) return;
    e.preventDefault();
    const raw = getPoint(e);
    const p = { x: snap(raw.x), y: snap(raw.y) };
    if (type === 'text') {
      setTextEdit({ x: p.x, y: p.y, value: '' });
      return;
    }
    const shape = makeDroppedShape(type, p, { stroke, strokeWidth, fillOn });
    if (!shape) return;
    commit([...shapes, shape]);
    setSelectedId(shape.id); // ready to move/resize immediately
    setTool('select');
  };

  // Double-click empty canvas → add a text label right there
  const handleCanvasDoubleClick = e => {
    if (textEdit) return;
    const raw = getPoint(e);
    setTextEdit({ x: snap(raw.x), y: snap(raw.y), value: '' });
  };

  const handleMove = e => {
    const drag = dragRef.current;
    if (!drag) return;
    const raw = getPoint(e);
    const p = { x: snap(raw.x), y: snap(raw.y) };

    if (drag.mode === 'draw') {
      setDraft(d =>
        d && d.type === 'pen'
          ? { ...d, points: [...d.points, raw] }
          : d
            ? { ...d, x2: p.x, y2: p.y }
            : d
      );
    } else if (drag.mode === 'move') {
      const dx = snap(raw.x - drag.start.x);
      const dy = snap(raw.y - drag.start.y);
      setShapes(prev => prev.map(s => (s.id === drag.orig.id ? translateShape(drag.orig, dx, dy) : s)));
    } else if (drag.mode === 'resize') {
      setShapes(prev =>
        prev.map(s => {
          if (s.id !== drag.orig.id) return s;
          const h = drag.handle;
          if (h === 'start') return { ...s, x1: p.x, y1: p.y };
          if (h === 'end') return { ...s, x2: p.x, y2: p.y };
          if (h.startsWith('pt-')) {
            // triangle vertex — move just that point
            const idx = parseInt(h.slice(3), 10);
            return { ...s, points: s.points.map((pt, i) => (i === idx ? { ...p } : pt)) };
          }
          // rect corners — move whichever stored coord is currently at that corner
          const xMin = s.x1 <= s.x2 ? 'x1' : 'x2';
          const xMax = s.x1 <= s.x2 ? 'x2' : 'x1';
          const yMin = s.y1 <= s.y2 ? 'y1' : 'y2';
          const yMax = s.y1 <= s.y2 ? 'y2' : 'y1';
          if (h === 'nw') return { ...s, [xMin]: p.x, [yMin]: p.y };
          if (h === 'ne') return { ...s, [xMax]: p.x, [yMin]: p.y };
          if (h === 'sw') return { ...s, [xMin]: p.x, [yMax]: p.y };
          if (h === 'se') return { ...s, [xMax]: p.x, [yMax]: p.y };
          return s;
        })
      );
    }
  };

  const handleUp = () => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag) return;
    if (drag.mode === 'draw' && draft) {
      const b = shapeBounds(draft);
      const tooSmall = draft.type !== 'pen' && b.maxX - b.minX < 4 && b.maxY - b.minY < 4;
      if (!tooSmall) {
        const final =
          draft.type === 'triangle' ? { ...draft, points: triangleToPoints(draft) } : draft;
        commit([...shapes, final]);
      }
      setDraft(null);
    } else if (drag.mode === 'move' || drag.mode === 'resize') {
      // shapes already updated live; push the pre-drag state into history
      setHistory(h => [...h.slice(-40), shapes.map(s => (s.id === drag.orig.id ? drag.orig : s))]);
    }
  };

  const undo = () => {
    setHistory(h => {
      if (!h.length) return h;
      setShapes(h[h.length - 1]);
      setSelectedId(null);
      return h.slice(0, -1);
    });
  };

  const deleteSelected = useCallback(() => {
    if (selectedId == null) return;
    commit(shapes.filter(s => s.id !== selectedId));
    setSelectedId(null);
  }, [selectedId, shapes, commit]);

  const duplicateSelected = useCallback(() => {
    const orig = shapes.find(s => s.id === selectedId);
    if (!orig) return;
    const copy = translateShape({ ...orig, id: nextId++ }, 20, 20);
    commit([...shapes, copy]);
    setSelectedId(copy.id);
  }, [selectedId, shapes, commit]);

  const clearAll = () => {
    if (!shapes.length) return;
    commit([]);
    setSelectedId(null);
  };

  // Keyboard: Backspace deletes, arrow keys nudge the selected shape
  useEffect(() => {
    const onKey = e => {
      if (selectedId == null || textEdit) return;
      if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        deleteSelected();
      } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        duplicateSelected();
      } else if (e.key.startsWith('Arrow')) {
        e.preventDefault();
        const dx = e.key === 'ArrowLeft' ? -GRID : e.key === 'ArrowRight' ? GRID : 0;
        const dy = e.key === 'ArrowUp' ? -GRID : e.key === 'ArrowDown' ? GRID : 0;
        setShapes(prev => prev.map(s => (s.id === selectedId ? translateShape(s, dx, dy) : s)));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId, textEdit, deleteSelected, duplicateSelected]);

  // Changing style while a shape is selected restyles it
  const restyleSelected = patch => {
    if (selectedId == null) return;
    commit(shapes.map(s => (s.id === selectedId ? { ...s, ...patch } : s)));
  };

  // ---- export ----
  useImperativeHandle(ref, () => ({
    isEmpty: () => shapes.length === 0,
    exportPng: () =>
      new Promise((resolve, reject) => {
        if (!shapes.length) return reject(new Error('empty'));
        let minX = Infinity,
          minY = Infinity,
          maxX = -Infinity,
          maxY = -Infinity;
        shapes.forEach(s => {
          const b = shapeBounds(s);
          minX = Math.min(minX, b.minX);
          minY = Math.min(minY, b.minY);
          maxX = Math.max(maxX, b.maxX);
          maxY = Math.max(maxY, b.maxY);
        });
        const pad = 24;
        const w = Math.max(60, maxX - minX + pad * 2);
        const h = Math.max(60, maxY - minY + pad * 2);

        const clone = svgRef.current.cloneNode(true);
        clone.querySelectorAll('[data-noexport]').forEach(n => n.remove());
        clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        clone.setAttribute('viewBox', `${minX - pad} ${minY - pad} ${w} ${h}`);
        clone.setAttribute('width', w);
        clone.setAttribute('height', h);

        const svgStr = new XMLSerializer().serializeToString(clone);
        const url = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgStr)))}`;
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = Math.ceil(w * EXPORT_SCALE);
          canvas.height = Math.ceil(h * EXPORT_SCALE);
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.scale(EXPORT_SCALE, EXPORT_SCALE);
          ctx.drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = () => reject(new Error('Could not rasterize drawing'));
        img.src = url;
      }),
  }));

  const selected = shapes.find(s => s.id === selectedId);

  return (
    <div className="draw-wrap">
      {/* Toolbar */}
      <div className="draw-toolbar">
        <div className="draw-tool-group">
          {TOOLS.map(t => (
            <button
              key={t.id}
              type="button"
              title={t.title}
              className={`draw-tool-btn ${tool === t.id ? 'active' : ''} ${t.draggable ? 'draw-tool-btn--draggable' : ''}`}
              draggable={t.draggable || undefined}
              onDragStart={
                t.draggable ? e => e.dataTransfer.setData('text/x-shape', t.id) : undefined
              }
              onClick={() => {
                setTool(t.id);
                if (t.id !== 'select') setSelectedId(null);
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="draw-tool-group">
          {COLORS.map(c => (
            <button
              key={c}
              type="button"
              title={c}
              className={`draw-color-btn ${stroke === c ? 'active' : ''}`}
              style={{ background: c }}
              onClick={() => {
                setStroke(c);
                restyleSelected({ stroke: c });
              }}
            />
          ))}
        </div>

        <div className="draw-tool-group">
          <select
            className="draw-select"
            title="Line thickness"
            value={strokeWidth}
            onChange={e => {
              const v = parseFloat(e.target.value);
              setStrokeWidth(v);
              restyleSelected({ strokeWidth: v });
            }}
          >
            <option value={1.5}>Thin</option>
            <option value={2.5}>Medium</option>
            <option value={4}>Thick</option>
          </select>
          <select
            className="draw-select"
            title="Text size"
            value={fontSize}
            onChange={e => setFontSize(parseInt(e.target.value, 10))}
          >
            {FONT_SIZES.map(f => (
              <option key={f} value={f}>
                {f === 14 ? 'Small text' : f === 20 ? 'Medium text' : 'Large text'}
              </option>
            ))}
          </select>
          <label className="draw-fill-toggle" title="Fill shapes with a light tint">
            <input
              type="checkbox"
              checked={fillOn}
              onChange={e => {
                setFillOn(e.target.checked);
                restyleSelected({ fillOn: e.target.checked });
              }}
            />
            Fill
          </label>
        </div>

        <div className="draw-tool-group draw-tool-group--right">
          <button type="button" className="draw-action-btn" onClick={undo} disabled={!history.length}>
            ↩ Undo
          </button>
          <button
            type="button"
            className="draw-action-btn"
            onClick={duplicateSelected}
            disabled={selectedId == null}
            title="Duplicate selected (Cmd/Ctrl+D)"
          >
            ⧉ Duplicate
          </button>
          <button
            type="button"
            className="draw-action-btn"
            onClick={deleteSelected}
            disabled={selectedId == null}
          >
            ✕ Delete
          </button>
          <button type="button" className="draw-action-btn" onClick={clearAll} disabled={!shapes.length}>
            Clear
          </button>
        </div>
      </div>

      {/* Canvas */}
      <div
        className="draw-canvas-frame"
        onDragOver={e => {
          if (e.dataTransfer.types.includes('text/x-shape')) e.preventDefault();
        }}
        onDrop={handleShapeDrop}
      >
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${H}`}
          className={`draw-canvas draw-canvas--${tool}`}
          onPointerDown={handleCanvasDown}
          onPointerMove={handleMove}
          onPointerUp={handleUp}
          onDoubleClick={handleCanvasDoubleClick}
        >
          <defs>
            {COLORS.map(c => (
              <marker
                key={c}
                id={`arrow-${c.slice(1)}`}
                viewBox="0 0 10 10"
                refX="9"
                refY="5"
                markerWidth="7"
                markerHeight="7"
                orient="auto-start-reverse"
              >
                <path d="M 0 0 L 10 5 L 0 10 z" fill={c} />
              </marker>
            ))}
            <pattern id="draw-grid" width={GRID * 2} height={GRID * 2} patternUnits="userSpaceOnUse">
              <circle cx="1" cy="1" r="1" fill="#e2e8f0" />
            </pattern>
          </defs>
          <rect data-noexport width={W} height={H} fill="url(#draw-grid)" />

          {shapes.map(s => (
            <g
              key={s.id}
              onPointerDown={e => handleShapeDown(e, s.id)}
              onDoubleClick={
                s.type === 'text'
                  ? e => {
                      e.stopPropagation();
                      setTextEdit({ x: s.x1, y: s.y1, value: s.text, id: s.id });
                    }
                  : undefined
              }
              style={{
                cursor: tool === 'select' ? 'move' : 'crosshair',
                // Unfilled SVG shapes only hit-test on their stroke by default —
                // 'visible' makes the whole interior grabbable for moving.
                pointerEvents: 'visible',
              }}
            >
              {/* invisible fat hit-area for thin lines */}
              {(s.type === 'line' || s.type === 'arrow') && (
                <line
                  data-noexport
                  x1={s.x1}
                  y1={s.y1}
                  x2={s.x2}
                  y2={s.y2}
                  stroke="transparent"
                  strokeWidth={14}
                />
              )}
              <ShapeEl s={s} />
            </g>
          ))}
          {draft && <ShapeEl s={draft} />}

          {selected && (
            <g data-noexport>
              <Handles s={selected} onHandleDown={handleHandleDown} />
            </g>
          )}
        </svg>

        {textEdit && (
          <input
            autoFocus
            className="draw-text-input"
            style={{ left: `${(textEdit.x / W) * 100}%`, top: `${(textEdit.y / H) * 100}%` }}
            value={textEdit.value}
            placeholder="Label…"
            onChange={e => setTextEdit({ ...textEdit, value: e.target.value })}
            onKeyDown={e => {
              if (e.key === 'Enter') commitText();
              if (e.key === 'Escape') setTextEdit(null);
            }}
            onBlur={commitText}
          />
        )}
      </div>

      <div className="draw-hint">
        <strong>Drag a shape button onto the canvas</strong> to drop it, or pick a tool and drag to
        draw • <strong>double-click anywhere</strong> to add text (double-click a label to edit it)
        • <strong>▲ Select</strong>, then click inside any shape and drag to move it — corner
        squares resize rectangles, triangle corners drag individually, arrow keys nudge •
        ⧉/Cmd+D duplicates, Backspace deletes
      </div>
    </div>
  );
});

export default DrawCanvas;
